if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.submitButton.removeAttribute('aria-haspopup');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const ugOrderNow = !!this.closest('product-info') && this.dataset.ugMiniIntent !== 'true';
        delete this.dataset.ugMiniIntent;
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        const sizeGroups = [...(picker?.querySelectorAll('[data-ug-size-option]') || [])];
        const missingSize = sizeGroups.find(group => {
          const select = group.querySelector('select');
          return select ? !select.value : !group.querySelector('input:checked');
        });
        if (missingSize || (this.dataset.ugRequiresSize === 'true' && (!sizeGroups.length || !this.variantIdInput.value))) {
          const message = this.dataset.ugSizeMessage || window.variantStrings.unavailable;
          this.handleErrorMessage(message);
          const target = missingSize || sizeGroups[0];
          target?.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
          target?.querySelector('input:not(:disabled),select')?.focus({preventScroll:true});
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.dispatchEvent(new CustomEvent('ug:cart-state', { detail: 'loading' }));

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const ugFlight = window.UGCartMotion?.capture(this);
        const formData = new FormData(this.form);
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const accessoryOffer = this.closest('product-info')?.querySelector('ug-accessory-offer');
        const accessoryItems = accessoryOffer?.selectedItems || [];
        if (accessoryItems.length) {
          const mainEntries = Array.from(formData.entries());
          for (const [key, value] of mainEntries) {
            if (key === 'id' || key === 'quantity' || key === 'selling_plan') {
              formData.delete(key);
              formData.append('items[0][' + key + ']', value);
            } else if (key.startsWith('properties[')) {
              formData.delete(key);
              formData.append('items[0][properties]' + key.slice(10), value);
            }
          }
          if (!mainEntries.some(([key]) => key === 'quantity')) formData.append('items[0][quantity]', quantity);
          accessoryItems.forEach((item, index) => {
            formData.append('items[' + (index + 1) + '][id]', item.id);
            formData.append('items[' + (index + 1) + '][quantity]', item.quantity);
          });
        }
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(routes.cart_add_url.endsWith('.js') ? routes.cart_add_url : routes.cart_add_url + '.js', config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            this.dispatchEvent(new CustomEvent('ug:cart-state', { detail: 'success' }));
            if (ugOrderNow) {
              window.location.assign(window.routes.cart_url + '#ug-order-contact-title');
              return;
            }
            // Refresh the bag without invoking Dawn's drawer/notification renderer.
            const iconHTML = response.sections?.['cart-icon-bubble'];
            const icon = document.getElementById('cart-icon-bubble');
            const freshIcon = iconHTML && new DOMParser().parseFromString(iconHTML, 'text/html').querySelector('.shopify-section');
            if (icon && freshIcon) icon.replaceChildren(...freshIcon.childNodes);
            window.UGCartMotion?.fly(ugFlight).catch(() => {});
            this.closest('quick-add-modal')?.hide(true);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Proizvod nije dodat. Proveri vezu i pokušaj ponovo.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            this.dispatchEvent(new CustomEvent('ug:cart-state', { detail: 'complete' }));
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

